import { Injectable } from '@angular/core';
import { AppFeatures } from 'src/app/core/app.features';
import { MenuItem } from 'src/app/core/models/main-menu.models';
import { TranslateService } from '@ngx-translate/core';
import { UserAccessLevel } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class MenuConfigService {
  constructor(private translate: TranslateService) {}

  /**
   * Returns default menu items for the given section.
   * The titles are translated using the TranslateService.
   * @param section - The menu section (e.g., 'analytics', 'admin', 'source-catalog')
   */
  getDefaultMenuItems(section: string): Array<MenuItem> {
    const config: { [key: string]: Array<MenuItem> } = {
      analytics: [
        {
          id: 1,
          title: this.translate.instant('menu.configure'),
          isHeader: true,
          key: 'analytics.configure',
          user_access_level: UserAccessLevel.CLIENT_USER,
          children: [
            {
              title: this.translate.instant('menu.home'),
              icon: ['fas', 'house'],
              link: 'analytics/home',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.workspaces'),
              icon: ['fas', 'folder'],
              link: 'analytics/workspaces',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.data_feeds'),
              icon: ['fas', 'rss'],
              link: 'analytics/feeds',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.dashboards'),
              icon: ['fas', 'tachometer-alt'],
              link: 'analytics/dashboards',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.dimensions'),
              icon: ['fas', 'layer-group'],
              link: 'analytics/dimensions',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.alerts'),
              icon: ['fas', 'bell'],
              link: 'analytics/alerts',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.export-schedules'),
              icon: ['fas', 'calendar-alt'],
              link: 'analytics/schedules',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.auto-story-runs'),
              icon: ['fas', 'wand-magic-sparkles'],
              link: 'analytics/auto-story-runs',
              user_access_level: UserAccessLevel.CLIENT_USER,
              permission: AppFeatures.ADVANCED_MASS_OPERATIONS_AI_AUTO_STORY,
            },
          ],
        },
        {
          id: 2,
          title: this.translate.instant('menu.monitor'),
          isHeader: true,
          key: 'analytics.monitor',
          user_access_level: UserAccessLevel.CLIENT_USER,
          children: [
            {
              title: this.translate.instant('menu.notifications-center'),
              icon: ['fas', 'flag'],
              link: 'analytics/notifications',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
            {
              title: this.translate.instant('menu.alerts-center'),
              icon: ['fas', 'bell'],
              link: 'analytics/alerts-center',
              user_access_level: UserAccessLevel.CLIENT_USER,
            },
          ],
        },
        {
          id: 3,
          title: this.translate.instant('menu.data'),
          isHeader: true,
          key: 'analytics.data',
          user_access_level: UserAccessLevel.CLIENT_ADMIN,

          children: [
            {
              title: this.translate.instant('menu.connectors'),
              icon: ['fas', 'plug'],
              link: 'analytics/connectors',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
          ],
        },
        // {
        //   id: 4,
        //   title: this.translate.instant('menu.settings'),
        //   isHeader: true,
        //   expanded: true,
        //   children: [
        //     {
        //       title: this.translate.instant('menu.account_settings'),
        //       icon: ['fas', 'cog'],
        //       link: 'analytics/settings',
        //     },
        //   ],
        // },
      ],
      admin: [
        {
          id: 6,
          title: this.translate.instant('menu.client_management'),
          isHeader: true,
          key: 'admin.client-management',
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.dashboard'),
              icon: ['fas', 'chart-line'],
              link: 'admin/dashboard',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.companies'),
              icon: ['fas', 'building'],
              link: 'admin/companies',
              // Mirrors the backend gate on client_admin/companies
              // (FeatureComponentGate::companyManager): the Company Manager
              // feature, or Global Admin and above without it. The grid is
              // company-scoped, so a Client Admin sees their own company and
              // reaches its Settings from here.
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
              permission: AppFeatures.COMPANY_MANAGER_ROOT,
              permission_bypass_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.data_categories'),
              icon: ['fas', 'tags'],
              link: 'admin/categories',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.objects'),
              icon: ['fas', 'layer-group'],
              link: 'admin/objects',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.export-templates'),
              icon: ['fas', 'file-export'],
              link: 'admin/export-templates',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.output-settings'),
              icon: ['fas', 'sliders'],
              link: 'admin/output-settings',
              // Temporarily Platform Admin (WAP-1638): the [Custom] per-object
              // rows still need their rename guard and a Used by list in the
              // dialog before Client / Global Admins get the entry.
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.view-presets'),
              icon: ['fas', 'table-columns'],
              link: 'admin/view-presets',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
          ],
        },
        {
          id: 7,
          title: this.translate.instant('menu.user_management'),
          isHeader: true,
          key: 'admin.user-management',
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.users'),
              icon: ['fas', 'user'],
              link: 'admin/home',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.user-sessions'),
              icon: ['fas', 'user-clock'],
              link: 'admin/user-sessions',
              // BE AuthGET::sessions rejects below GLOBAL_ADMIN - showing the
              // entry to lower levels dead-ends on the guard.
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.user_groups'),
              icon: ['fas', 'users-cog'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
              link: 'admin/groups',
            },
            {
              title: this.translate.instant('menu.roles'),
              icon: ['fas', 'user-shield'],
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
              link: 'admin/roles',
            },
          ],
        },
        {
          id: 16,
          title: this.translate.instant('menu.data-management'),
          isHeader: true,
          key: 'admin.data-management',
          // Header floor stays at the lowest child level (see the sysadmin
          // System Management note): both children are GLOBAL_ADMIN.
          user_access_level: UserAccessLevel.GLOBAL_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.connectors'),
              icon: ['fas', 'plug'],
              link: 'admin/connectors',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.connector-rules'),
              icon: ['fas', 'bullseye'],
              link: 'admin/rules',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.platform-identities'),
              icon: ['fas', 'user-tag'],
              link: ['/admin', 'platform-identities'],
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
          ],
        },
        {
          id: 10,
          title: this.translate.instant('menu.automations'),
          isHeader: true,
          key: 'admin.automations',
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.export-schedule'),
              icon: ['fas', 'calendar-alt'],
              link: 'admin/export-schedules',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.schedules'),
              icon: ['fas', 'calendar-check'],
              link: 'admin/schedules',
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
          ],
        },
        {
          id: 8,
          title: this.translate.instant('menu.audit-trails'),
          isHeader: true,
          key: 'admin.audit-trails',
          user_access_level: UserAccessLevel.CLIENT_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.activity_logs'),
              icon: ['fas', 'clipboard-list'],
              link: ['/admin', 'activity-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.search_logs'),
              icon: ['fas', 'search'],
              link: ['/admin', 'search-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.output_logs'),
              icon: ['fas', 'file-export'],
              link: ['/admin', 'output-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.synopsis_logs'),
              icon: ['fas', 'book-open'],
              link: ['/admin', 'synopsis-logs'],
              user_access_level: UserAccessLevel.CLIENT_ADMIN,
            },
            {
              title: this.translate.instant('menu.admin-operations'),
              icon: ['fas', 'clipboard-check'],
              link: ['/admin', 'operations'],
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.developer_api_company_list'),
              icon: ['fas', 'code'],
              link: ['/admin', 'developer-api-company-list'],
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant(
                'menu.developer_api_company_usage_list',
              ),
              icon: ['fas', 'code'],
              link: ['/admin', 'developer-api-company-usage-list'],
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
      ],
      sysadmin: [
        {
          id: 18,
          title: this.translate.instant('menu.system_monitoring'),
          isHeader: true,
          key: 'sysadmin.system-monitoring',
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.dashboard'),
              icon: ['fas', 'chart-line'],
              link: 'sysadmin/dashboard',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.log-event-stats'),
              icon: ['fas', 'wave-square'],
              link: 'sysadmin/log-event-stats',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.agent-sessions'),
              icon: ['fas', 'robot'],
              link: 'sysadmin/agent-sessions',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.sysadmin-operations'),
              icon: ['fas', 'clipboard-check'],
              link: 'sysadmin/operations',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
        {
          id: 12,
          title: this.translate.instant('menu.system_management'),
          isHeader: true,
          key: 'sysadmin.system-management',
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.worker-management'),
              icon: ['fas', 'microchip'],
              link: 'sysadmin/worker-management',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.cron-jobs'),
              icon: ['fas', 'clock'],
              link: 'sysadmin/cron-jobs',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.maintenance'),
              icon: ['fas', 'screwdriver-wrench'],
              link: 'sysadmin/maintenance',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.solr-servers'),
              icon: ['fas', 'server'],
              link: 'sysadmin/solr-servers',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.system-configuration'),
              icon: ['fas', 'sliders'],
              link: 'sysadmin/system-configuration',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
        {
          id: 17,
          title: this.translate.instant('menu.vdc_inventory'),
          isHeader: true,
          key: 'sysadmin.vdc-inventory',
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.infra-hosts'),
              icon: ['fas', 'network-wired'],
              link: 'sysadmin/infra-hosts',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.infra-host-findings'),
              icon: ['fas', 'triangle-exclamation'],
              link: 'sysadmin/infra-host-findings',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.infra-monitoring-profiles'),
              icon: ['fas', 'clipboard-check'],
              link: 'sysadmin/infra-monitoring-profiles',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
        {
          id: 13,
          title: this.translate.instant('menu.app_management'),
          isHeader: true,
          key: 'sysadmin.app-management',
          // Header lowered to GLOBAL_ADMIN so level-50 admins reach the Use Case
          // Templates child below; filterMenuByAccessLevel drops a header for any
          // user under its level, taking its children with it, so the header floor
          // must not exceed its lowest child. The SYSTEM_ADMIN children stay hidden
          // from level-50 by their own per-node level (proposal §12 Option A).
          user_access_level: UserAccessLevel.GLOBAL_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.internationalization'),
              icon: ['fas', 'globe'],
              link: 'sysadmin/translations',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.help-manager'),
              icon: ['fas', 'circle-info'],
              link: 'sysadmin/help-manager',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.nomenclatures'),
              icon: ['fas', 'hashtag'],
              link: 'sysadmin/nomenclature-management',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.object-properties'),
              icon: ['fas', 'table-list'],
              link: 'sysadmin/object-property-management',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.use-case-templates'),
              icon: ['fas', 'wand-magic-sparkles'],
              link: 'sysadmin/use-case-templates',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
          ],
        },
      ],
      'mobile-admin': [
        {
          id: 14,
          title: this.translate.instant('menu.mobile_app_management'),
          isHeader: true,
          key: 'sysadmin.mobile-app-management',
          user_access_level: UserAccessLevel.PLATFORM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.dashboard'),
              icon: ['fas', 'chart-line'],
              link: 'mobile-admin/dashboard',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.mobile_app_settings'),
              icon: ['fas', 'sliders'],
              link: 'mobile-admin/settings',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
            {
              title: this.translate.instant('menu.mobile_app_strings'),
              icon: ['fas', 'language'],
              link: 'mobile-admin/strings',
              user_access_level: UserAccessLevel.PLATFORM_ADMIN,
            },
          ],
        },
      ],
      'source-catalog': [
        {
          id: 15,
          title: this.translate.instant('menu.monitor'),
          isHeader: true,
          key: 'sysadmin.monitor',
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.dashboard'),
              icon: ['fas', 'chart-line'],
              link: 'source-catalog/dashboard',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.http-request-log'),
              icon: ['fas', 'exchange-alt'],
              link: 'source-catalog/http-request-log',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.harvest-queue'),
              icon: ['fas', 'layer-group'],
              link: 'source-catalog/harvest-queue',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.harvest-log'),
              icon: ['fas', 'list-check'],
              link: 'source-catalog/harvest-log',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
        {
          id: 19,
          title: this.translate.instant('menu.audit-trail'),
          isHeader: true,
          key: 'sysadmin.audit-trail',
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.import-content-logs'),
              icon: ['fas', 'history'],
              link: 'source-catalog/import-content-logs',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.source-catalog-operations'),
              icon: ['fas', 'clipboard-check'],
              link: 'source-catalog/operations',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
        {
          id: 11,
          title: this.translate.instant('menu.tools'),
          isHeader: true,
          key: 'sysadmin.tools',
          user_access_level: UserAccessLevel.SYSTEM_ADMIN,
          children: [
            {
              title: this.translate.instant('menu.url-playground'),
              icon: ['fas', 'flask'],
              link: 'source-catalog/url-playground',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.harvest-remediation'),
              icon: ['fas', 'wand-magic-sparkles'],
              link: 'source-catalog/harvest-remediation',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.harvest-targets'),
              icon: ['fas', 'crosshairs'],
              link: 'source-catalog/harvest-targets',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.harvest-routing'),
              icon: ['fas', 'route'],
              link: 'source-catalog/harvest-routing',
              user_access_level: UserAccessLevel.GLOBAL_ADMIN,
            },
            {
              title: this.translate.instant('menu.sitemaps'),
              icon: ['fas', 'sitemap'],
              link: 'source-catalog/sitemaps',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.sitemap-mode-log'),
              icon: ['fas', 'clock-rotate-left'],
              link: 'source-catalog/sitemap-mode-log',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.banned-urls'),
              icon: ['fas', 'ban'],
              link: 'source-catalog/banned-urls',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
            {
              title: this.translate.instant('menu.proxies'),
              icon: ['fas', 'globe'],
              link: 'source-catalog/proxies',
              user_access_level: UserAccessLevel.SYSTEM_ADMIN,
            },
          ],
        },
      ],
    };

    return config[section] || [];
  }
}
